import React from 'react';
import { Layout } from '@components/commons';
import { GetServerSidePropsContext } from 'next';
import {
  CreatePublicationArgs,
  UserPublication,
  useUserPublications,
} from '@contexts/PropertyListingPublications/gql/hooks';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import { formatDistance } from 'date-fns';
import { es } from 'date-fns/locale';
import { Container, Paper } from '@mui/material';
import { useRouter } from 'next/router';
import { CreatePublicationModal } from '@contexts/PropertyListingPublications/components/create_publication_modal';
import { UserTypes, withAuth } from '@contexts/Shared';

export const getServerSideProps = async ({
  locale,
}: GetServerSidePropsContext) => {
  return {
    props: {
      messages: (await import(`../../locales/${locale?.toLowerCase()}.json`))
        .default,
    },
  };
};

type DialogCreatePublicationProps = {
  open: boolean;
};

function DialogCreatePublication(props: DialogCreatePublicationProps) {
  const router = useRouter();
  const [open, setOpen] = React.useState(props.open);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCreated = async (
    data: CreatePublicationArgs & { id: string }
  ) => {
    handleClose();

    return router.push(`/listing-publications/${data.id}/pre_qualification`);
  };

  return (
    <>
      <Button variant="contained" onClick={handleClickOpen}>
        Crear publicación
      </Button>
      <CreatePublicationModal
        open={open}
        onClose={handleClose}
        onCreated={handleCreated}
      />
    </>
  );
}

type PublicationCardProps = {
  publication: UserPublication;
};

function PublicationCard(props: PublicationCardProps) {
  const { publication } = props;

  const router = useRouter();

  const handleClick = () => {
    const baseUrl = `/listing-publications/${publication.id}`;

    if (publication.lastKnownStep !== null) {
      return router.push(`${baseUrl}/${publication.lastKnownStep}`);
    } else {
      return router.push(`${baseUrl}/pre_qualification`);
    }
  };

  return (
    <Card sx={{ minWidth: 275 }}>
      <CardContent>
        <Typography variant="h6" component="div" gutterBottom>
          {publication.name || publication.address || 'Sin nombre'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Última vez actualizada hace{' '}
          {formatDistance(publication.updatedAt, new Date(), {
            locale: es,
          })}
        </Typography>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={handleClick}>
          Actualizar
        </Button>
      </CardActions>
    </Card>
  );
}

function PublicationsTable() {
  const userPublications = useUserPublications();
  const router = useRouter();

  const shouldOpenModal = router.query.create !== undefined;

  const nonEmptyPublications = userPublications.data.map((publication) => (
    <Grid item xs={12} md={6} lg={4} key={publication.id}>
      <PublicationCard publication={publication} />
    </Grid>
  ));

  const emptyPublications = (
    <Grid item xs={12}>
      <Paper>
        <Container>
          <Typography variant="body1">
            No tienes publicaciones creadas
          </Typography>
        </Container>
      </Paper>
    </Grid>
  );

  return (
    <Grid container spacing={3} style={{ padding: 20 }}>
      <Grid item xs={12}>
        <Typography variant="h4">Publicaciones</Typography>
      </Grid>

      {userPublications.data.length > 0
        ? nonEmptyPublications
        : emptyPublications}

      <Grid item xs={12}>
        <DialogCreatePublication open={shouldOpenModal} />
      </Grid>
    </Grid>
  );
}

const ListingPublicationsPage = () => {
  return (
    <Layout>
      <PublicationsTable />
    </Layout>
  );
};

export default withAuth(ListingPublicationsPage, UserTypes.landlord);
